import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {UserSubscription} from "../../../user/user-subscription";
import {User} from "../../../user/user";
import {DbServerErrors} from "../../../shared/interceptors/db-server-errors";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {AuthService} from "../../auth.service";
import {Utils} from "../../../shared/utils/utils";
import {DbValidators} from "../../../shared/validators/DbValidators";
import * as fromStore from "../../../store";
import {filter, first, skip, switchMap, takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {combineLatest, Subject, Subscription} from "rxjs";
import {Payment} from "../../../user/payment";
import {PaymentMethod} from "../../../user/payment-method";
import {Location} from "@angular/common";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {Config} from "../../../config/config";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {NotifierComponent} from "../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../shared/components/notifier/notification";
import {NotificationType} from "../../../shared/components/notifier/notification-type";
import {WorkspaceService} from "../../../workspace/workspace.service";
import {TextUtils} from "../../../shared/utils/text-utils";
import {LanguageService} from "../../../shared/services/language/language.service";
import {ResellerCidService} from "../../../shared/services/reseller-cid/reseller-cid.service";

@Component({
    selector: 'db-checkout-reseller',
    templateUrl: './checkout-reseller.component.html',
    styleUrls: ['./checkout-reseller.component.scss'],
    standalone: false
})
export class CheckoutResellerComponent implements OnInit, OnDestroy {

    @Input() user: User;
  @Input() renew: boolean = false;
  @Input() recharge: boolean = false;
  @Input() upgradeSubscriptionId?: number;
  public subscriptions: Subscription[];
  public choosedSubscription: UserSubscription;
  public currentSubscription: UserSubscription;
  public loading: boolean = false;
  public loadingStripe: boolean = false;
  public isMobile: boolean = window.innerWidth <= 991;
  public couponCode: string;
  public stripe: any = null;
  public subscriptionId: number;
  public productId: number;
  public period: 'monthly' | 'yearly';
  public btnClicked: boolean;
  public monthlyPlan: UserSubscription;
  public yearlyPlan: UserSubscription;
  public selectedPeriod: 'monthly' | 'yearly';
  public payments: Payment[];
  public userLocalStorage: any;
  public checkoutForm: FormGroup;
  public hasServerFieldErrors: boolean;
  public userDoesNotExist: boolean = true;
  public formSubmitted: boolean = false;
  public emailChecked: boolean = false;
  public showPassword: boolean = false;
  public serverErrors: DbServerErrors;
  public showPWDRequirements: boolean = false;
  public progress: number = 0;
  public planId: number;
  public stripeErrors: any;
  public step: number = 1;
  private _stripe: any = null;
  private _checkout: any;
  public companyInfo: any;
  public proRatedCreditLoading: boolean;
  public proRatedCredits: number;
  public paymentMethods: PaymentMethod[];
  public selectedPaymentMethod: PaymentMethod;
  public showAddNewPaymentMethod: boolean = false;
  public activeTab: string = 'existingCards';
  public invitationData: any;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public paypalOrderId: string;
  public paypalCurrency: string;
  public paypalAmount: string;
  public paypalOrderApproved: boolean = false;
  public paypalSetupToken: string;
  private _paypalSdk: any = null;
  private _paypalSession: any = null;
  public paypalFieldsReady: boolean = false;
  public isPayPalProvider: boolean = false;
  private paypalAgencyPaymentMethodId: string = null;
  private _checkStripeAvailability(): boolean {
    return !!(window['Stripe'] && typeof window['Stripe'] === 'function');
  }

  private _loadStripeScript(): void {
    // Check if Stripe script is already loaded
    if (this._checkStripeAvailability()) {
      this._initStripe();
      return;
    }

    // Load Stripe script dynamically
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => {
      this._initStripe();
    };
    script.onerror = () => {
      this.loadingStripe = false;
      NotifierComponent.notify(new Notification(
          'Failed to load payment system. Please refresh the page and try again.',
          NotificationType.DANGER
      ));
    };
    
    document.head.appendChild(script);
  }

  private _checkPayPalAvailability(): boolean {
    return !!(window['paypal'] && typeof window['paypal'] === 'object');
  }

  private async _loadPayPalScript(): Promise<boolean> {

    if (this._checkPayPalAvailability()) {
      return true;
    }

    return new Promise<boolean>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = Config.paypalIsProduction
        ? 'https://www.paypal.com/web-sdk/v6/core'
        : 'https://www.sandbox.paypal.com/web-sdk/v6/core';
      script.async = true;

      script.onload = () => {
        resolve(true);
      };

      script.onerror = () => {
        this.loadingStripe = false;
        this.stripeErrors = 'Failed to load PayPal payment system.';
        NotifierComponent.notify(new Notification(
          'Failed to load PayPal payment system. Please refresh the page and try again.',
          NotificationType.DANGER
        ));
        reject(false);
      };

      document.head.appendChild(script);
    });
  }

  private async _initPayPalCheckout(clientId: string, saveOnly: boolean = false): Promise<void> {

    if (!window['paypal']) {
      throw new Error('PayPal SDK not loaded');
    }

    if (typeof window['paypal'].createInstance !== 'function') {
      throw new Error('PayPal v6 SDK createInstance not available');
    }

    try {
      // Clean up any existing PayPal fields before initializing
      const paypalFieldIds = ['paypal-card-number-field', 'paypal-card-expiry-field', 'paypal-card-cvv-field'];
      paypalFieldIds.forEach(fieldId => {
        const field = document.getElementById(fieldId);
        if (field) {
          while (field.firstChild) {
            field.removeChild(field.firstChild);
          }
        }
      });

      // Destroy old session and SDK if they exist
      if (this._paypalSession) {
        this._paypalSession = null;
      }
      if (this._paypalSdk) {
        this._paypalSdk = null;
      }
      this.paypalFieldsReady = false;

      this._paypalSdk = await window['paypal'].createInstance({
        clientId: clientId,
        components: ["card-fields"]
      });

      // Choose session type based on context
      if (saveOnly) {
        // For paid-to-paid (adding new payment method to existing subscription)
        this._paypalSession = this._paypalSdk.createCardFieldsSavePaymentSession();
      } else {
        // For checkout flow (save with purchase)
        this._paypalSession = this._paypalSdk.createCardFieldsOneTimePaymentSession();
      }

      if (!this._paypalSession) {
        throw new Error('Failed to create PayPal session');
      }

      await this._renderPayPalFields();
    } catch (error) {
      throw error;
    }
  }

  private async _renderPayPalFields(): Promise<void> {
    try {
      if (!this._paypalSession) {
        throw new Error('PayPal session not created');
      }

      // Clean up existing fields first - remove all children properly
      const paypalFields = ['paypal-card-number-field', 'paypal-card-expiry-field', 'paypal-card-cvv-field'];
      paypalFields.forEach(fieldId => {
        const field = document.getElementById(fieldId);
        if (field) {
          // Remove all child nodes one by one
          while (field.firstChild) {
            field.removeChild(field.firstChild);
          }
        }
      });

      const fieldStyle = {
        input: {
          fontSize: '14px',
          fontFamily: 'inherit',
          color: '#000000',
          height: '44px',
          padding: '12px 16px',
          borderRadius: '8px',
          border: '1px solid rgba(118, 118, 128, 0.16)',
          background: '#FFFFFF',
          transition: 'border-color 0.2s ease',
          outline: 'none'
        },
        'input.focus': {
          border: '1px solid #000000',
          outline: 'none'
        },
        '.invalid': {
          border: '1px solid #D41125',
        },
        '.invalid.focus': {
          border: '1px solid #D41125',
        }
      };

      // Create PayPal v6 card field components
      const numberField = this._paypalSession.createCardFieldsComponent({
        type: 'number',
        selector: '#paypal-card-number-field',
        placeholder: TextUtils.getText('text_cardNumber'),
        style: fieldStyle
      });

      const expiryField = this._paypalSession.createCardFieldsComponent({
        type: 'expiry',
        selector: '#paypal-card-expiry-field',
        placeholder: 'MM/YYYY',
        style: fieldStyle
      });

      const cvvField = this._paypalSession.createCardFieldsComponent({
        type: 'cvv',
        selector: '#paypal-card-cvv-field',
        placeholder: 'CID/CVV',
        style: fieldStyle
      });

      // Append fields to DOM containers
      document.querySelector("#paypal-card-number-field").appendChild(numberField);
      document.querySelector("#paypal-card-expiry-field").appendChild(expiryField);
      document.querySelector("#paypal-card-cvv-field").appendChild(cvvField);

      this.paypalFieldsReady = true;
      this.loadingStripe = false;

      if (!this.user && this.step === 1) {
        setTimeout(() => {
          this.step = 2;
        }, 500);
      }
    } catch (error) {
      this.loadingStripe = false;
      this.stripeErrors = 'Failed to render PayPal payment fields.';
    }
  }

  private fetchPayPalClientId = (): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const clientId = this.companyInfo?.payment_provider?.account_id;

      if (clientId) {
        resolve(clientId);
      } else {
        reject(new Error('PayPal client ID not found in payment provider configuration'));
      }
    });
  }

  private fetchPayPalSetupToken = async (): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      this._workspaceService.addPaymentMethod()
        .pipe(
          filter(response => !!response),
          first()
        )
        .subscribe({
          next: (response) => {
            const body = response?.response?.body?.setup_intent;

            if (body?.setup_token) {
              this.paypalSetupToken = body.setup_token;
              resolve(body.setup_token);
            } else {
              reject(new Error('Invalid PayPal response data'));
            }
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  public confirmPayPalPayment = async (saveOnly: boolean = false): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!this.paypalFieldsReady) {
        return {
          success: false,
          error: 'Payment fields are still loading. Please wait a moment and try again.'
        };
      }

      if (!this._paypalSession || typeof this._paypalSession.submit !== 'function') {
        return {
          success: false,
          error: 'Payment system not initialized. Please try again.'
        };
      }

      let state, data;

      if (saveOnly) {
        // Save payment method only (paid-to-paid)
        const setupToken = await this.fetchPayPalSetupToken();
        const result = await this._paypalSession.submit(setupToken);
        state = result.state;
        data = result.data;
      } else {
        // Save with purchase (checkout flow)
        const result = await this._paypalSession.submit(this.paypalOrderId, {
          billingAddress: {
            postalCode: this.checkoutForm?.get('companyZipCode')?.value || '00000'
          }
        });
        state = result.state;
        data = result.data;
      }

      // Check liability shift in all possible locations
      const threeDsIndicators = {
        liability_shift: data?.liabilityShift
          ?? data?.liability_shift
          ?? data?.authentication_result?.liability_shift
          ?? data?.three_d_secure?.liability_shift
          ?? null,
      };

      const liabilityShift = threeDsIndicators?.liability_shift
        ? String(threeDsIndicators.liability_shift).toUpperCase()
        : null;

      switch (state) {
        case 'succeeded':
          // Hard rejection for explicit authentication failure
          if (liabilityShift === 'NO') {
            NotifierComponent.notify(new Notification(
              '3D Secure authentication failed. Please use another card.',
              NotificationType.DANGER
            ));
            return {
              success: false,
              error: '3D Secure authentication failed'
            };
          }

          // Soft rejection for uncertain/unavailable authentication - allow retry
          if (liabilityShift === 'UNKNOWN') {
            NotifierComponent.notify(new Notification(
              '3D Secure authentication unavailable. Please try again or use another card.',
              NotificationType.DANGER
            ));
            return {
              success: false,
              error: '3D Secure authentication unavailable'
            };
          }

          if (saveOnly) {
            // Store payment method on backend
            const storeResponse = await this._workspaceService
              .storePaymentMethod({ vaultSetupToken: data.vaultSetupToken })
              .pipe(
                filter(res => !!res),
                first()
              )
              .toPromise();

            if (storeResponse && storeResponse.success) {
              if (storeResponse?.response?.body?.agency_payment_method_id) {
                this.paypalAgencyPaymentMethodId = storeResponse.response.body.agency_payment_method_id;
              }
              return { success: true };
            } else {
              const errorMessage = storeResponse?.serverErrors?.message || 'Failed to save payment method. Please try again.';
              return {
                success: false,
                error: errorMessage
              };
            }
          } else {
            this.paypalOrderApproved = true;
            return { success: true };
          }

        case 'canceled':
          NotifierComponent.notify(new Notification(
            'Payment was canceled.',
            NotificationType.DANGER
          ));
          return {
            success: false,
            error: 'Payment canceled. Please try again.'
          };

        case 'failed':
          // Check if failure was due to 3DS authentication
          if (liabilityShift === 'NO') {
            NotifierComponent.notify(new Notification(
              '3D Secure authentication failed. Please use another card.',
              NotificationType.DANGER
            ));
            return {
              success: false,
              error: '3D Secure authentication failed'
            };
          }

          if (liabilityShift === 'UNKNOWN') {
            NotifierComponent.notify(new Notification(
              '3D Secure authentication unavailable. Please try again or use another card.',
              NotificationType.DANGER
            ));
            return {
              success: false,
              error: '3D Secure authentication unavailable'
            };
          }

          // Generic failure
          NotifierComponent.notify(new Notification(
            data?.message || 'Payment failed. Please try again.',
            NotificationType.DANGER
          ));
          return {
            success: false,
            error: data?.message || 'Payment failed. Please try again.'
          };

        case 'requires_action':
        case 'payer_action_required':
          NotifierComponent.notify(new Notification(
            'Additional verification is required to complete 3D Secure. Please follow the prompt and try again.',
            NotificationType.DANGER
          ));
          return {
            success: false,
            error: '3D Secure verification requires additional action.'
          };

        default:
          return {
            success: false,
            error: 'Unexpected payment state. Please try again.'
          };
      }

    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to process payment. Please try again.'
      };
    }
  }

    constructor(
        private _store: Store<fromStore.DbStoreState>,
        private _authService: AuthService,
        private _router: Router,
        private _route: ActivatedRoute,
        private _location: Location,
        private _workspaceService: WorkspaceService,
        private _languageService: LanguageService,
        private _resellerCidService: ResellerCidService,
    ) {
    }

  ngOnInit(): void {
    const queryParams: any = (<any>this._route.queryParams).value;
    const queryParamsIndex: number = this._router.url.indexOf('?');
    const utm_source = queryParams.utm_source ? "?utm_source=" + queryParams.utm_source : (queryParams["amp;utm_source"] ? "?utm_source=" + queryParams["amp;utm_source"] : "");
    const utm_medium = queryParams.utm_medium ? "&utm_medium=" + queryParams.utm_medium : (queryParams["amp;utm_medium"] ? (queryParams["amp;utm_source"] ? "&utm_medium=" + queryParams["amp;utm_medium"] : "?utm_medium=" + queryParams["amp;utm_medium"]) : "");
    const planId: number = queryParams && queryParams.plan_id ? parseInt(queryParams.plan_id, 10) : null;
    this.planId = planId;
    const couponValue = queryParams.coupon ? queryParams.coupon : null;

    // Handle invitation data
    this.invitationData = queryParams.invitation_data || new URL(window.location.href).searchParams.get("invitation_data");
    if (this.invitationData) {
      this.invitationData = Utils.b64DecodeUnicode(this.invitationData);
    }

    // Support both old and new query param formats
    const subscriptionId: number = queryParams && queryParams.subscr_id ? parseInt(queryParams.subscr_id, 10) : this.upgradeSubscriptionId;
    const productId: number = queryParams && queryParams.product_id ? parseInt(queryParams.product_id, 10) : null;
    const period: 'monthly' | 'yearly' = queryParams && queryParams.period ? queryParams.period : null;

    this.subscriptionId = this.upgradeSubscriptionId ? this.upgradeSubscriptionId : subscriptionId;
    this.productId = productId;
    this.period = period;

    if (queryParamsIndex !== -1) {
      let queryString = utm_source + utm_medium;
      if (productId && period) {
        queryString += (queryString ? '&' : '?') + 'product_id=' + productId + '&period=' + period;
      } else if (subscriptionId) {
        queryString += (queryString ? '&' : '?') + 'subscr_id=' + subscriptionId;
      }
      queryString += (planId ? '&plan_id=' + planId : '') + (couponValue ? '&coupon=' + couponValue : '');
      this._location.replaceState(this._router.url.substring(0, queryParamsIndex) + queryString);
    }
    this._setFormOptions();
    this._addEventHandlers();
    this._getCompanyInfo();

    // Try to load Stripe script if not already available
    if (!this._checkStripeAvailability()) {
      this._loadStripeScript();
    }
  }

  public progressPercent(): void {
    this.progress = Utils.progressPercent(this.checkoutForm.controls['password']);
  }

  public resetEmailCheck(): void {
    // Reset email check state when user types
    this.emailChecked = false;
    this.userDoesNotExist = true;

    // Clear invitation error if it exists
    const emailControl = this.checkoutForm.get('email');
    if (emailControl?.errors) {
      const {invitationError, ...remainingErrors} = emailControl.errors;
      emailControl.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
    }
  }

  public onTosConsentChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const control = this.checkoutForm.get('terms_privacy_consent_accepted');
    if (control) {
      control.setValue(checkbox.checked);
    }
  }

  public async togglePeriod(period: 'monthly' | 'yearly'): Promise<void> {
    if (this.selectedPeriod === period) return;

    this.selectedPeriod = period;

    // Update choosedSubscription based on selected period
    if (period === 'monthly' && this.monthlyPlan) {
      this.choosedSubscription = this.monthlyPlan;
    } else if (period === 'yearly' && this.yearlyPlan) {
      this.choosedSubscription = this.yearlyPlan;
    }

    // Update form with new subscription ID
    this.checkoutForm.get('subscriptionId').setValue(
      this.user ? this.choosedSubscription?.corePlanId : this.choosedSubscription?.plan?.agency_pricing_plan_id
    );

    if (this.step === 2) {
      this._resetStripeCheckout();
      await this.initCheckout();
    }
  }
  private _resetStripeCheckout(): void {
    if (this._checkout) {
      this._checkout = null;
    }

    const paymentElement = document.getElementById('payment-element');
    if (paymentElement) {
      paymentElement.innerHTML = '';
    }

    // Reset loading state
    this.loadingStripe = false;
  }

  public goBackToStep1(): void {
    // Clean up PayPal fields - remove all children properly
    const paypalFields = ['paypal-card-number-field', 'paypal-card-expiry-field', 'paypal-card-cvv-field'];
    paypalFields.forEach(fieldId => {
      const field = document.getElementById(fieldId);
      if (field) {
        // Remove all child nodes one by one
        while (field.firstChild) {
          field.removeChild(field.firstChild);
        }
      }
    });

    // Reset PayPal state
    this._paypalSession = null;
    this._paypalSdk = null;
    this.paypalFieldsReady = false;
    this.paypalOrderApproved = false;

    // Clean up Stripe
    this._resetStripeCheckout();

    // Go back to step 1
    this.step = 1;
  }

  public hasBothPlans(): boolean {
    return !!(this.monthlyPlan && this.yearlyPlan);
  }

  private _setFormOptions(): void {
    this.checkoutForm = new FormGroup({
      'firstName': new FormControl('', [
        Validators.required,
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, { link: true }, true)
      ]),
      'lastName': new FormControl('', [
        Validators.required,
        Utils.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, { link: true }, true)
      ]),
      'email': new FormControl('', [DbValidators.trim, Validators.required, Validators.email, Validators.pattern(/^[+_\-a-zA-Z0-9]+(\.[+_\-a-zA-Z0-9]+)*@[+_\-a-zA-Z0-9]+(\.[+_\-A-Za-z0-9]+)*(\.[+_\-A-Za-z]{2,24})$/)]),
      'password': new FormControl('',
          [
            Validators.required,
            Validators.minLength(8),
            DbValidators.trim,
            Utils.patternValidator(/\d/, { hasNumber: true }),
            Utils.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            Utils.patternValidator(/[a-z]/, { hasSmallCase: true })
          ]),
      'subscriptionId': new FormControl(''),
      'stripe_customer_id': new FormControl(''),
      'provider': new FormControl('stripe_connect'), // 'stripe_connect' or 'paypal'
      'payment_provider_checkout_key': new FormControl(''),
      'type': new FormControl('agency_suite_customer')
    });
    if (this.user && this.user.email) {
      this.checkoutForm.controls['email'].setValue(this.user.email);
    }
    // Prefill email from invitation data if available
    if (this.invitationData && this.invitationData.client_email) {
      this.checkoutForm.controls['email'].setValue(this.invitationData.client_email);
    }
  }


  private _addEventHandlers( ): void {
    this.subscriptions = [];

    // Determine which selector to use based on whether we have product_id and period
    const subscriptionSelector = this.productId && this.period
      ? this._store.select(fromStore.getUserAvailableSubscriptionByProduct(this.productId, this.period, false, 'platform'))
      : this._store.select(fromStore.getUserAvailableSubscription(this.subscriptionId, false, 'platform'));

    // If we have productId, also fetch both monthly and yearly plans for toggle
    const monthlyPlanSelector = this.productId
      ? this._store.select(fromStore.getUserAvailableSubscriptionByProduct(this.productId, 'monthly', false, 'platform'))
      : null;

    const yearlyPlanSelector = this.productId
      ? this._store.select(fromStore.getUserAvailableSubscriptionByProduct(this.productId, 'yearly', false, 'platform'))
      : null;

    // Initialize selectedPeriod
    this.selectedPeriod = this.period || 'monthly';

    this.subscriptions.push(
        this._store.select(fromStore.getUserLoaded)
            .pipe(
                filter((loaded: boolean) => loaded),
                switchMap(() => {
                  // If we have productId, fetch both monthly and yearly plans
                  if (this.productId && monthlyPlanSelector && yearlyPlanSelector) {
                    return combineLatest([
                      this._store.select(fromStore.getUser),
                      this._store.select(fromStore.getUserSubscription),
                      this._store.select(fromStore.getUserLocalStorage),
                      subscriptionSelector,
                      monthlyPlanSelector,
                      yearlyPlanSelector
                    ]);
                  } else {
                    return combineLatest([
                      this._store.select(fromStore.getUser),
                      this._store.select(fromStore.getUserSubscription),
                      this._store.select(fromStore.getUserLocalStorage),
                      subscriptionSelector
                    ]);
                  }
                }),
                takeUntil(this._destroyStream$)
            )
            .subscribe
            ((data: any[]) => {
              const user = data[0];
              const userSubscription = data[1];
              const userLocalStorage = data[2];
              const userAvailableSubscription = data[3];
              const monthlyPlan = data[4] || null;
              const yearlyPlan = data[5] || null;

              // Store monthly and yearly plans if available
              this.monthlyPlan = monthlyPlan;
              this.yearlyPlan = yearlyPlan;

              // Redirect to 404 if subscription is not found
              if (!this.renew && !this.recharge && !userAvailableSubscription  && !user) {
                  this._router.navigate(['/404'], {skipLocationChange: true, replaceUrl: true});
                  return;
              }

              this.user = user;
              this._getPaymentMethods();

              if (user && this.currentSubscription?.trialClient) {
                  if (this._stripe ) {
                      this.initExistingCheckout();
                  }
              }
              this.currentSubscription = userSubscription;
              this.userLocalStorage = userLocalStorage;

              !this.renew && this._getProRatedCredits();
              if (this.renew || this.recharge) {
                this.choosedSubscription = userSubscription;
              } else {
                this.choosedSubscription = userAvailableSubscription;
              }
              this.checkoutForm.get('subscriptionId').setValue(this.user ? this.choosedSubscription?.corePlanId : this.choosedSubscription?.plan?.agency_pricing_plan_id);
            })
    );
  }


    private _getProRatedCredits(): void {
        if (!this.currentSubscription?.trialClient && this.currentSubscription?.agencyCustomerData) {
            this.proRatedCreditLoading = true;
            this._workspaceService.getAgencyClientsProRatedCredits(this.currentSubscription.clientId)
                .pipe(
                    filter((response: DbResponse): boolean => !!response),
                    first()
                )
                .subscribe((response: DbResponse): void => {
                    if (response.success) {
                        this.proRatedCredits = response.data?.credit_amount?.amount;
                    }
                    this.proRatedCreditLoading = false;
                });
        }
    }

  private _getPaymentMethods(): void {
    if (this.user && this.user.id) {
      this._store.dispatch(new fromStore.LoadUserPaymentMethods());
      this._store.select(fromStore.getUserPaymentMethods)
          .pipe(
              filter((paymentMethods: any) => !!paymentMethods && paymentMethods.length > 0),
              first(),
              takeUntil(this._destroyStream$)
          )
          .subscribe((paymentMethods: PaymentMethod[]) => {
              this.paymentMethods = paymentMethods;
              this.selectedPaymentMethod = paymentMethods.find((pm: PaymentMethod) => pm.active) || paymentMethods[0];
          });
    }
  }

    public async changeTab(tabId: string) {
        this.activeTab = tabId;
        this.showAddNewPaymentMethod = tabId === 'addCard';
        this.paypalAgencyPaymentMethodId = null;
        if (this.showAddNewPaymentMethod) {
            // Clean up existing checkout first
            if (this._checkout) {
                this._checkout = null;
            }

            // Clean up PayPal session
            if (this._paypalSession) {
                this._paypalSession = null;
            }
            this.paypalFieldsReady = false;

            // Clean up payment element
            const paymentElement = document.getElementById('payment-element');
            if (paymentElement) {
                paymentElement.innerHTML = '';
            }

            // Clean up PayPal card fields - remove all children properly
            const paypalFieldIds = ['paypal-card-number-field', 'paypal-card-expiry-field', 'paypal-card-cvv-field'];
            paypalFieldIds.forEach(fieldId => {
                const field = document.getElementById(fieldId);
                if (field) {
                    // Remove all child nodes one by one
                    while (field.firstChild) {
                        field.removeChild(field.firstChild);
                    }
                }
            });

            // Remove any Stripe iframes
            const stripeIframes = document.querySelectorAll('iframe[name^="__privateStripeFrame"]');
            stripeIframes.forEach(iframe => {
                iframe.remove();
            });

            // Wait for Angular to render the payment element before initializing
            this.loadingStripe = true;
            setTimeout(async () => {
                try {
                    // Get setup intent client secret (this determines provider)
                    const clientSecret = await this.fetchSetupClientSecret();

                    if (!clientSecret) {
                        throw new Error('Failed to get client secret from server');
                    }

                    // Check if PayPal provider
                    if (this.isPayPalProvider) {
                        // PayPal v6 SDK flow (save payment method only)
                        // Get client ID from provider data
                        const clientId = await this.fetchPayPalClientId();
                        await this._loadPayPalScript();
                        await this._initPayPalCheckout(clientId, true); // saveOnly = true
                    } else {
                        // Stripe flow
                        // Ensure Stripe is initialized before trying to use it
                        if (!this._stripe) {
                            this._initStripe();
                        }

                        // Wait for Stripe to be initialized if it wasn't already
                        if (!this._stripe) {
                            this.loadingStripe = false;
                            NotifierComponent.notify(new Notification(
                                'Failed to load payment system. Please refresh the page and try again.',
                                NotificationType.DANGER
                            ));
                            return;
                        }

                        // Initialize Stripe elements with client secret (like db-payment-method)
                        this._checkout = this._stripe.elements({
                            clientSecret: clientSecret,
                            appearance: {
                                theme: 'stripe'
                            }
                        });

                        // Create and mount payment element
                        const paymentEl = this._checkout.create('payment');
                        if (paymentEl) {
                            paymentEl.on('ready', () => {
                                this.loadingStripe = false;
                            });

                            await paymentEl.mount('#payment-element');
                        } else {
                            this.loadingStripe = false;
                        }
                    }
                } catch (error) {
                    this.loadingStripe = false;
                }
            }, 100);
        } else {
            // Clean up payment systems when toggling back to existing payment method
            this.loadingStripe = false;

            if (this._checkout) {
                this._checkout = null;
            }

            if (this._paypalSession) {
                this._paypalSession = null;
            }
            this.paypalFieldsReady = false;

            // Clean up payment element
            const paymentElement = document.getElementById('payment-element');
            if (paymentElement) {
                paymentElement.innerHTML = '';
            }

            // Clean up PayPal card fields - remove all children properly
            const paypalFieldIds = ['paypal-card-number-field', 'paypal-card-expiry-field', 'paypal-card-cvv-field'];
            paypalFieldIds.forEach(fieldId => {
                const field = document.getElementById(fieldId);
                if (field) {
                    // Remove all child nodes one by one
                    while (field.firstChild) {
                        field.removeChild(field.firstChild);
                    }
                }
            });

            // Remove any Stripe iframes
            const stripeIframes = document.querySelectorAll('iframe[name^="__privateStripeFrame"]');
            stripeIframes.forEach(iframe => {
                iframe.remove();
            });
        }
    }

  private _getCompanyInfo(): void {
    this._store.select(fromStore.getCompanyInfoLoaded)
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => this._store.select(fromStore.getCompanyInfo)),
            takeUntil(this._destroyStream$)
        )
        .subscribe((companyInfo: any) => {
          this.companyInfo = companyInfo;
          // Add TOS consent control if required
          if (this.companyInfo?.branding?.require_tos_consent) {
            this.checkoutForm.addControl('terms_privacy_consent_accepted', new FormControl(false));
          }
          // Initialize Stripe after getting company info
           !this.renew && !this.currentSubscription?.platformPaidClient && this._initStripe();
        });
  }

  private _initStripe(): void {
    this._initStripeWithRetry(0);
  }

  private _initStripeWithRetry(attempt: number): void {
    if (window['Stripe']) {
      try {
        const stripeConfig: any = {};

        let stripeAccountId = null;
        if (this.companyInfo?.payment_provider?.provider === 'stripe_connect' && this.companyInfo?.payment_provider?.account_id) {
          stripeAccountId = this.companyInfo.payment_provider.account_id;
        } else if (this.companyInfo?.stripe?.account_id) {
          stripeAccountId = this.companyInfo.stripe.account_id;
        }

        if (stripeAccountId) {
          stripeConfig.stripeAccount = stripeAccountId;
        }

        // Set locale from language service
        const currentLanguage = this._languageService.getCurrentLanguage();
        if (currentLanguage) {
          stripeConfig.locale = currentLanguage;
        }

        this._stripe = window['Stripe'](Config.stripeKey, stripeConfig);
        if (this.user && !this._checkout) {
            this.initExistingCheckout();
        }
      } catch (error) {
          this.loadingStripe = false;
      }
    } else {
        this.loadingStripe = false;
    }
  }

  private fetchClientSecret = () => {
    return new Promise<string>((resolve, reject) => {
      // Get the pricing plan ID from the chosen subscription
      // When using product_id/period, we won't have subscriptionId from query params
      const pricingPlanId = this.choosedSubscription?.plan?.agency_pricing_plan_id || this.subscriptionId;

      if (!pricingPlanId) {
        reject(new Error('No pricing plan ID available'));
        return;
      }

      // Get existing customer_id from form to reuse customer
      const existingCustomerId = this.checkoutForm.controls['stripe_customer_id'].value;

      // Use different endpoint based on whether user exists
      const checkoutSessionRequest = this.user
        ? this._authService.getUserCheckoutSession(pricingPlanId,
              this.checkoutForm.controls['email'].value,
              this.checkoutForm.controls['firstName'].value,
              this.checkoutForm.controls['lastName'].value,
              existingCustomerId)
        : this._authService.getCheckoutSession(pricingPlanId,
              this.checkoutForm.controls['email'].value,
              this.checkoutForm.controls['firstName'].value,
              this.checkoutForm.controls['lastName'].value,
              this.invitationData?.invitation_key,
              this.invitationData?.workspace_invitation_id,
              existingCustomerId);

      checkoutSessionRequest
          .pipe(
              filter(response => !!response),
              first()
          )
          .subscribe({
            next: (response) => {
              const body = response?.response?.body;
              if (body?.customer_id) {
                this.checkoutForm.get('stripe_customer_id').setValue(body.customer_id);
              }
              const provider = body?.provider || 'stripe_connect';
              this.checkoutForm.get('provider').setValue(provider);

              if (provider === 'paypal' && body?.paypal_order_id) {
                this.isPayPalProvider = true;
                this.paypalOrderId = body.paypal_order_id;
                this.paypalCurrency = body.currency || 'USD';
                this.paypalAmount = body.amount;
                this.checkoutForm.get('payment_provider_checkout_key').setValue(body.paypal_order_id);
                resolve('paypal_checkout');
              } else if (provider === 'stripe_connect' && body?.session_id) {
                this.isPayPalProvider = false;
                this.checkoutForm.get('payment_provider_checkout_key').setValue(body.session_id);

                if (body?.client_secret) {
                  resolve(body.client_secret);
                } else {
                  reject(new Error('Invalid Stripe response data'));
                }
              } else if (body?.client_secret) {
                resolve(body.client_secret);
              } else {
                const invitationErrorData = response?.serverErrors?.fields?.invitation;

                if (invitationErrorData) {
                  const invitationError = Array.isArray(invitationErrorData)
                    ? invitationErrorData[0]
                    : invitationErrorData;
                  this.checkoutForm.get('email').setErrors({
                    invitationError: `${invitationError} ${TextUtils.getText('text_contactSupport')}`
                  });

                  reject(new Error(invitationError));
                } else {
                  reject(new Error('Invalid response data'));
                }
              }
            },
            error: (err) => {

              // Check for invitation errors
              const invitationErrorData = err?.response?.body?.errors?.invitation
                || err?.response?.body?.serverErrors?.fields?.invitation;

              if (invitationErrorData) {
                const invitationError = Array.isArray(invitationErrorData)
                  ? invitationErrorData[0]
                  : invitationErrorData;

                // Set error on email field with invitation message and support text
                this.checkoutForm.get('email').setErrors({
                  invitationError: `${invitationError} ${TextUtils.getText('text_contactSupport')}`
                });

                reject(new Error(invitationError));
              } else {
                reject(err);
              }
            }
          });
    });
  }

  private fetchSetupClientSecret = () => {
    return new Promise<string>((resolve, reject) => {
      // Check if PayPal is the provider
      const provider = this.companyInfo?.payment_provider?.provider;

      if (provider === 'paypal') {
        // PayPal flow: Get setup token
        this._workspaceService.addPaymentMethod()
            .pipe(
                filter(response => !!response),
                first()
            )
            .subscribe({
              next: (response) => {
                const body = response?.response?.body?.setup_intent;

                if (body?.setup_token) {
                  this.isPayPalProvider = true;
                  this.paypalSetupToken = body.setup_token;
                  resolve(this.paypalSetupToken);
                } else {
                  reject(new Error('Invalid PayPal response data'));
                }
              },
              error: (err) => {
                reject(err);
              }
            });
      } else {
        // Stripe flow: Get setup intent client secret (original logic)
        this._workspaceService.addPaymentMethod()
            .pipe(
                filter(response => !!response),
                first()
            )
            .subscribe({
              next: (response) => {
                if (response && response?.response?.body?.setup_intent?.client_secret) {
                  this.isPayPalProvider = false;
                  resolve(response.response.body.setup_intent.client_secret);
                } else {
                  reject(new Error('Invalid response data'));
                }
              },
              error: (err) => {
                reject(err);
              }
            });
      }
    });
  }

  private async _waitForPaymentElement(): Promise<void> {
    return new Promise((resolve) => {
      const checkElement = () => {
        const paymentElementContainer = document.getElementById('payment-element');
        if (paymentElementContainer && paymentElementContainer.offsetParent !== null) {
          resolve();
        } else {
          setTimeout(checkElement, 100);
        }
      };
      checkElement();
    });
  }

  public async initExistingCheckout() {
      this.loadingStripe = true;
      await this._initCheckoutWithRetry(0);
  }

  private async _initCheckoutWithRetry(attempt: number): Promise<void> {
      try {
          // For renew/recharge or non-trial users, use setup mode instead of payment mode
          const useSetupMode = (this.renew || this.recharge || this.currentSubscription?.platformPaidClient) && this.activeTab === 'addCard';

          // Check if PayPal provider by fetching client secret once
          let clientSecret;
          if (useSetupMode) {
              clientSecret = await this.fetchSetupClientSecret();
          } else {
              clientSecret = await this.fetchClientSecret();
          }

          // Check if PayPal provider
          if (this.isPayPalProvider) {
              // Load PayPal v6 SDK and initialize with client ID
              // Get client ID from provider data for paid-to-paid
              const clientId = await this.fetchPayPalClientId();
              await this._loadPayPalScript();
              await this._initPayPalCheckout(clientId, useSetupMode);
              return;
          }

          // Stripe flow
          // Check if Stripe is initialized
          if (!this._stripe) {
              this.loadingStripe = false;
              return;
          }

          await this._waitForPaymentElement();

          if (useSetupMode) {
              // Use setup mode - pass cached client secret
              this._checkout = await this._stripe.initCheckout({
                fetchClientSecret: () => Promise.resolve(clientSecret),
                elementsOptions: {
                  savedPaymentMethod: {
                    enableRedisplay: 'auto'
                  }
                }
              });

              // Create payment element using the same method as db-payment-method component
              const paymentElement = this._checkout.create('payment');
              if (paymentElement) {
                  // Listen for the ready event to confirm element is mounted
                  paymentElement.on('ready', () => {
                      this.loadingStripe = false;
                  });

                  await paymentElement.mount('#payment-element');
              } else {
                  this.loadingStripe = false;
              }
          } else {
              // Use payment mode - pass cached client secret
              this._checkout = await this._stripe.initCheckout({
                fetchClientSecret: () => Promise.resolve(clientSecret),
                elementsOptions: {
                  savedPaymentMethod: {
                    enableRedisplay: 'auto'
                  }
                }
              });

              const paymentElement = this._checkout.createPaymentElement();
              if (paymentElement) {
                  // Listen for the ready event to confirm element is mounted
                  paymentElement.on('ready', () => {
                      this.loadingStripe = false;
                  });

                  await paymentElement.mount('#payment-element');
              } else {
                  this.loadingStripe = false;
              }
          }
      } catch (error) {
          this.loadingStripe = false;
      }
  }

  public async initCheckout() {
    // Check email first
    if (this.checkoutForm.controls['email'].valid && !this.user) {
      this.loadingStripe = true;

      // Check if email exists
      this._authService.checkUserExists(this.checkoutForm.value.email)
        .pipe(
          filter((response: DbResponse): boolean => !!response),
          first()
        )
        .subscribe(async (response) => {
          this.emailChecked = true;
          this.userDoesNotExist = response.success;

          // If email already exists, stop and show error
          if (!this.userDoesNotExist) {
            this.loadingStripe = false;
            return;
          }

          // Email is valid and doesn't exist, proceed with checkout
          try {
            // Fetch client secret once (which will also set provider and PayPal data if applicable)
            const clientSecret = await this.fetchClientSecret();

            // Check if PayPal provider
            if (this.isPayPalProvider) {
              // Load PayPal v6 SDK and initialize with client ID
              const clientId = await this.fetchPayPalClientId();
              await this._loadPayPalScript();
              await this._initPayPalCheckout(clientId);
            } else {
              // Stripe flow - pass cached client secret
              this._checkout = await this._stripe.initCheckout({
                fetchClientSecret: () => Promise.resolve(clientSecret),
                elementsOptions: {
                  savedPaymentMethod: {
                    enableRedisplay: 'auto'
                  }
                }
              });
              const paymentElement = this._checkout.createPaymentElement();
              if (paymentElement) {
                // Listen for the ready event to confirm element is mounted
                paymentElement.on('ready', () => {
                  this.loadingStripe = false;
                });

                paymentElement.mount('#payment-element');
                setTimeout(() => {
                  this.step = 2;
                }, 500);
              }
            }
          } catch (error) {
            this.loadingStripe = false;
          }
        });
    } else {
      // If form is invalid or user already exists, don't proceed
      this.formSubmitted = true;
    }
  }

  public async checkout() {
    this.loading = true;

      const gtag = window['gtag'];
      if (typeof gtag === 'function') {
          const resellerCid = this._resellerCidService.get();
          gtag("event", "checkout_button_click", {
              twdb_user_id: this.user && this.user.id || 0,
              ...(resellerCid ? { cid: resellerCid } : {}),
          });
      }
    if (this.user) {
      // For existing users, use Stripe Checkout session
      await this._checkoutExistingUser();
    } else {
      // For new users, use the existing flow
      await this._checkoutNewUser();
    }
  }

  private async _checkoutExistingUser(): Promise<void> {
    try {
      if ((this.renew || this.recharge || this.currentSubscription?.platformPaidClient || this.currentSubscription?.isAgency) && this.selectedPaymentMethod && !this.showAddNewPaymentMethod) {
        this._upgradeWithExistingPaymentMethod();
      } else {
        const useSetupMode = this.renew || this.recharge || this.currentSubscription?.platformPaidClient;
        if (this.isPayPalProvider) {
          if (!this.paypalOrderApproved) {
            const result = await this.confirmPayPalPayment(useSetupMode);

            if (result.success) {
              if (useSetupMode) {
                this._upgradeWithExistingPaymentMethod();
              } else {
                this._upgradeWithCheckoutSession();
              }
            } else {
              this.loading = false;
              if (result.error) {
                NotifierComponent.notify(new Notification(
                  result.error,
                  NotificationType.DANGER
                ));
              }
            }
            return;
          }
          this._upgradeWithCheckoutSession();
        } else {
          if (useSetupMode) {
            const result = await this._stripe.confirmSetup({
              elements: this._checkout,
              confirmParams: {
                return_url: `${window.location.origin}/?payment-complete`,
              },
              redirect: "if_required",
            });

            if (result.error) {
              this.stripeErrors = result.error.message;
              this.loading = false;
              NotifierComponent.notify(new Notification(result.error.message, NotificationType.DANGER));
            } else {
              const paymentMethodId = result.setupIntent?.payment_method;

              if (paymentMethodId) {
                this._upgradeWithExistingPaymentMethod(paymentMethodId);
              } else {
                this.loading = false;
                NotifierComponent.notify(new Notification('Failed to save payment method. Please try again.', NotificationType.DANGER));
              }
            }
          } else {
            // Use confirm for payment mode (charging immediately)
            const result = await this._checkout.confirm({
              returnUrl: `${window.location.origin}/?payment-complete`,
              redirect: "if_required",
            });

            if (result.type === 'error') {
              this.stripeErrors = result.error.message;
              this.loading = false;
              NotifierComponent.notify(new Notification(result.error.message, NotificationType.DANGER));
            } else {
              // Payment successful, proceed with upgrade
              this._upgradeWithCheckoutSession();
            }
          }
        }
      }
    } catch (error) {
      this.loading = false;
      NotifierComponent.notify(new Notification('Payment failed. Please try again.', NotificationType.DANGER));
    }
  }

  private _upgradeWithExistingPaymentMethod(paymentId?): void {
    // Determine payment type and payment method ID based on whether it's existing or new payment method
    const isExistingPaymentMethod = !paymentId;
    const paymentType = 'agency_suite_customer';
    const pricingPlanId = this.renew ? this.currentSubscription?.plan?.agency_pricing_plan_id : this.choosedSubscription?.plan?.agency_pricing_plan_id;

    const stripePaymentMethodId = this.paypalAgencyPaymentMethodId
      ? this.paypalAgencyPaymentMethodId
      : (isExistingPaymentMethod ? this.selectedPaymentMethod?.id.toString() : paymentId);

    const upgradeType = this.renew ? 'renew' : null;

        this._workspaceService.upgradeSubscription(
            this.choosedSubscription?.corePlanId,
            null, // additionalWebsitesCount
            null, // couponCode
            null, // card_id for new payment method
            paymentType,
            this.choosedSubscription?.subscriptionTitle, // subscriptionTitle
            null, // paypalPaymentId
            null, // paypalPayerId
            0, // productId
            null, // pricingType
            null, // boosterHostingAddOnChecked
            this.choosedSubscription?.plan?.planId, // planId
            pricingPlanId, // pricingPlanId for agency_suite_customer
            stripePaymentMethodId, // stripe_payment_method_id for existing payment method
            upgradeType // upgrade_type: 'renew' only for renew case
        )
            .pipe(
                filter((response: DbResponse): boolean => !!response),
                first()
            )
            .subscribe((response): void => {
                if (response.success) {
                    this._store.dispatch(new fromStore.UpdateUserSubscription());

                    const stripeStatus = response.data?.result?.stripe_subscription_latest_invoice_pi_status;
                    const clientSecret = response.data?.result?.stripe_subscription_pi_client_secret;
                    const paymentMethodId = response.data?.result?.agency_payment_method_id;
                    if (stripeStatus === 'requires_payment_method') {
                      this.loading = false;
                      this.serverErrors = response.serverErrors;
                      NotifierComponent.notify(new Notification(
                          response.serverErrors?.message || 'Upgrade failed. Please try again.',
                          NotificationType.DANGER
                      ));
                    }
                    else {
                      if (stripeStatus === 'requires_action' && clientSecret) {
                        this._handle3DSAuthentication(clientSecret, paymentMethodId);
                      } else {
                        this.currentSubscription?.trialClient && this.sendEvent('purchase');

                        this._store.dispatch(new fromStore.UpdateUserSubscription());
                        this._router.navigate(['/websites'], {
                          queryParams: {
                            upgraded_account: 1,
                          }
                        });
                      }
                    }
                } else {
                    this.loading = false;
                    this.serverErrors = response.serverErrors;
                    NotifierComponent.notify(new Notification(
                        response.serverErrors?.message || 'Upgrade failed. Please try again.',
                        NotificationType.DANGER
                    ));
                }
            });
    }

    private _handle3DSAuthentication(clientSecret: string, paymentMethodId: string): void {
        if (!this._stripe) {
            this._initStripeFor3DS().then(() => {
                this._stripe.confirmCardPayment(clientSecret, {
                    payment_method: paymentMethodId
                }).then((result) => {
                    if (result.error) {
                        this.loading = false;
                        NotifierComponent.notify(new Notification(
                            result.error.message || '3DS authentication failed. Please try again.',
                            NotificationType.DANGER
                        ));
                    } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
                        this.currentSubscription?.trialClient && this.sendEvent('purchase');

                        this._store.dispatch(new fromStore.UpdateUserSubscription());
                        this._router.navigate(['/websites'], {
                            queryParams: {
                                upgraded_account: 1,
                            }
                        });
                    } else {
                        this.loading = false;
                        NotifierComponent.notify(new Notification(
                            'Authentication incomplete. Please try again.',
                            NotificationType.DANGER
                        ));
                    }
                }).catch((error) => {
                    this.loading = false;
                    NotifierComponent.notify(new Notification(
                        'Authentication failed. Please try again.',
                        NotificationType.DANGER
                    ));
                });
            }).catch((error) => {
                this.loading = false;
                NotifierComponent.notify(new Notification(
                    'Payment system not ready. Please try again.',
                    NotificationType.DANGER
                ));
            });
        } else {
            this._stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethodId
            }).then((result) => {
                if (result.error) {
                    this.loading = false;
                    NotifierComponent.notify(new Notification(
                        result.error.message || '3DS authentication failed. Please try again.',
                        NotificationType.DANGER
                    ));
                } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
                    this.currentSubscription?.trialClient && this.sendEvent('purchase');

                    this._store.dispatch(new fromStore.UpdateUserSubscription());
                    this._router.navigate(['/websites'], {
                        queryParams: {
                            upgraded_account: 1,
                        }
                    });
                } else {
                    this.loading = false;
                    NotifierComponent.notify(new Notification(
                        'Authentication incomplete. Please try again.',
                        NotificationType.DANGER
                    ));
                }
            }).catch((error) => {
                this.loading = false;
                NotifierComponent.notify(new Notification(
                    'Authentication failed. Please try again.',
                    NotificationType.DANGER
                ));
            });
        }
    }


    private async _initStripeFor3DS(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (window['Stripe']) {
                try {
                    const stripeConfig: any = {};
                    let stripeAccountId = null;
                    if (this.companyInfo?.payment_provider?.provider === 'stripe_connect' && this.companyInfo?.payment_provider?.account_id) {
                      stripeAccountId = this.companyInfo.payment_provider.account_id;
                    } else if (this.companyInfo?.stripe?.account_id) {
                      stripeAccountId = this.companyInfo.stripe.account_id;
                    }

                    if (stripeAccountId) {
                      stripeConfig.stripeAccount = stripeAccountId;
                    }

                    // Set locale from language service
                    const currentLanguage = this._languageService.getCurrentLanguage();
                    if (currentLanguage) {
                      stripeConfig.locale = currentLanguage;
                    }

                    this._stripe = window['Stripe'](Config.stripeKey, stripeConfig);
                    resolve();
                } catch (error) {
                    reject(error);
                }
            } else {
                reject(new Error('Stripe not available'));
            }
        });
    }


  private async _checkoutNewUser(): Promise<void> {
    try {
      if (this.isPayPalProvider) {
        if (!this.paypalOrderApproved) {
          const result = await this.confirmPayPalPayment();
          if (result.success) {
            this._checkoutRequest();
          } else {
            this.loading = false;
            if (result.error) {
              NotifierComponent.notify(new Notification(
                result.error,
                NotificationType.DANGER
              ));
            }
          }
          return;
        }
        this._checkoutRequest();
      } else {
        const result = await this._checkout.confirm({
          returnUrl: `${window.location.origin}/?payment-complete`,
          redirect: "if_required",
        });

        if (result.type === 'error') {
          this.stripeErrors = result.error.message;
          this.loading = false;
          NotifierComponent.notify(new Notification(result.error.message, NotificationType.DANGER));
        } else {
          this._checkoutRequest();
        }
      }
    } catch (error) {
      this.loading = false;
      NotifierComponent.notify(new Notification('Payment failed. Please try again.', NotificationType.DANGER));
    }
  }

  private _upgradeWithCheckoutSession(): void {
    const provider = this.checkoutForm.get('provider').value;
    const paymentProviderCheckoutKey = this.checkoutForm.get('payment_provider_checkout_key').value;

    if (!paymentProviderCheckoutKey) {
      this.loading = false;
      NotifierComponent.notify(new Notification('Payment session not found. Please try again.', NotificationType.DANGER));
      return;
    }
    this._workspaceService.upgradeSubscriptionWithCheckoutSession(
      this.choosedSubscription?.corePlanId,
      null, // additionalWebsitesCount
      null, // couponCode
      provider,
      paymentProviderCheckoutKey,
      this.choosedSubscription?.subscriptionTitle,
      null, // paypalPaymentId
      null, // paypalPayerId
      0, // productId
      null, // pricingType
      null, // boosterHostingAddOnChecked
      this.choosedSubscription?.plan?.planId
    )
    .pipe(
      filter((response: DbResponse): boolean => !!response),
      first()
    )
    .subscribe((response): void => {
      if (response.success) {
        this.currentSubscription?.trialClient && this.sendEvent('purchase');

        this._store.dispatch(new fromStore.UpdateUserSubscription());
        this._router.navigate(['/websites'], {
          queryParams: {
            upgraded_account: 1,
            upgraded_from_trial: this.choosedSubscription && this.choosedSubscription.category === 'starter',
            upgraded_from_booster: (this.choosedSubscription && this.choosedSubscription.trialBoosterClient) || (this.choosedSubscription && this.choosedSubscription.boosterProClient),
            upgraded_from_api_reseller: this.choosedSubscription && this.choosedSubscription.apiResellerFree
          }
        });
      } else {
        this.loading = false;
        this.serverErrors = response.serverErrors;
        NotifierComponent.notify(new Notification(
          response.serverErrors?.message || 'Upgrade failed. Please try again.',
          NotificationType.DANGER
        ));
      }
    });
  }

  private _checkoutRequest() {
    this._authService.checkout(
      this.checkoutForm.getRawValue(),
      this.invitationData?.workspace_invitation_id || null,
      this.invitationData?.invitation_key || null
    )
        .pipe(
            filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response) => {
          if (response.success) {
              this.loginAfterCheckout();
          } else if (response.serverErrors && TypeUtils.isEmpty(response.serverErrors.fields)) {
            this.loading = false;
            this.serverErrors = response.serverErrors;
              NotifierComponent.notify(new Notification(
                  response.serverErrors.message,
                  NotificationType.DANGER
              ));
          } else {
            this.loading = false;
            this.serverErrors = response.serverErrors;
            this.checkHasServerFieldErrors(response.serverErrors);
          }
        });
  }

  private checkHasServerFieldErrors(serverErrors: DbServerErrors): void {
    const FIELDS_TO_CHECK = [
      'name',
      'first_name',
      'last_name',
      'email',
      'password',
      'stripe_token',
      'stripe_customer_id',
      'terms_privacy_consent_accepted',
    ] as const;

    type CheckedField = typeof FIELDS_TO_CHECK[number];

    const fields = serverErrors?.fields as Partial<Record<CheckedField, string>> | undefined;

    const errorMessages: string[] = [];
    if (fields) {
      for (const f of FIELDS_TO_CHECK) {
        const msg = fields[f];
        if (msg) errorMessages.push(msg);
      }
    }

    this.hasServerFieldErrors = errorMessages.length > 0;

    if (this.hasServerFieldErrors) {
      NotifierComponent.notify(
        new Notification(errorMessages.join('. '), NotificationType.DANGER, true),
      );
    }
  }

  public loginAfterCheckout(): void {
    const loginData = {
      'email': this.checkoutForm.controls['email'].value,
      'password': this.checkoutForm.controls['password'].value
    };
    this.loginUser(loginData);
  }

  public loginUser(loginData): void {
    this._authService.login(loginData)
        .pipe(
            filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response) => {
          if (response.success) {
            this._store.dispatch(new fromStore.UpdateUserSubscription());
            this._store.select(fromStore.getUserSubscription)
              .pipe(
                  filter((subscription: UserSubscription) => !!subscription),
                  first()
              )
              .subscribe(() => {
                  if (this.invitationData && this.invitationData.workspace_invitation_id) {
                    localStorage.setItem('dbFromInvitation', '1');
                  }
                  this._router.navigate(['/websites']);
              });
          }
          else {
            this._router.navigateByUrl('/login');
          }
        });
  }

  ngOnDestroy(): void {
    if (this._checkout) {
      this._checkout = null;
    }
    if (this._stripe) {
      this._stripe = null;
    }
    const stripeIframes = document.querySelectorAll('iframe[name^="__privateStripeFrame"]');
    stripeIframes.forEach(iframe => {
      iframe.remove();
    });
    const paymentElement = document.getElementById('payment-element');
    if (paymentElement) {
      paymentElement.innerHTML = '';
    }

    this.removeEventHandlers();
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private removeEventHandlers(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription: Subscription): void => {
        subscription.unsubscribe();
      });
      this.subscriptions = null;
    }
  }

  private sendEvent(eventTitle: string) {
      const gtag = window['gtag'];
      if (typeof gtag === 'function') {
          gtag("event", eventTitle, {
              twdb_user_id: this.user && this.user.id || 0
          });
      }
  }
}
